import { useCallback, useMemo } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../hooks";
import { ReviEvaluationsApi } from "../../../../../api/revi/ReviEvaluationsApi";

import { getTherapistIdFromUser } from "./utils";
import { useEvaluationsLists } from "./hooks/useEvaluationsLists";
import { useEvaluationDetail } from "./hooks/useEvaluationDetail";
import { useEvaluationWizardState } from "./hooks/useEvaluationWizardState";
import { useSharedEvaluations } from "./hooks/useSharedEvaluations";

export function useEvaluacionesController() {
  const { t } = useTranslation("therapist");
  const { accessToken, user } = useAuth();

  const therapistId = useMemo(() => getTherapistIdFromUser(user), [user]);
  const evalApi = useMemo(() => new ReviEvaluationsApi(), []);

  // LISTAS (standard + my + filtros)
  const lists = useEvaluationsLists({ accessToken, therapistId, evalApi });

  // DETALLE (modal ver + ensure full)
  const detail = useEvaluationDetail({ accessToken, evalApi });

  // WIZARD (crear/editar + ensure full)
  const wizard = useEvaluationWizardState({ accessToken, evalApi });

  // SHARED (marketplace + clone)
  const shared = useSharedEvaluations({
    accessToken,
    evalApi,
    onAfterClone: lists.refreshMy,
  });

  const onWizardSuccess = useCallback(async () => {
    wizard.closeWizard();
    await lists.refreshMy();
    message.success(t("evaluaciones.avisos.guardada"));
  }, [wizard, lists, t]);

  const onDelete = useCallback(
    async (ev) => {
      if (!accessToken || !ev?._id) return;
      try {
        await evalApi.deleteOrArchiveEvaluation(accessToken, ev._id);
        message.success(t("evaluaciones.avisos.eliminada"));
        await lists.refreshMy();
      } catch (e) {
        console.error(e);
        // El mensaje del backend llega ya redactado: solo se traduce el
        // respaldo de cuando no viene ninguno.
        message.error(e?.message || t("evaluaciones.avisos.errorEliminar"));
      }
    },
    [accessToken, evalApi, lists, t],
  );

  // 🔁 API EXACTA que consume el Page (mantenemos keys)
  return {
    // filtros / tabs
    tab: lists.tab,
    setTab: lists.setTab,
    area: lists.area,
    setArea: lists.setArea,
    search: lists.search,
    setSearch: lists.setSearch,

    // listas
    loading: lists.loading,
    standard: lists.standard,
    myEvals: lists.myEvals,

    // detalle
    selected: detail.selected,
    setSelected: detail.setSelected, // compat
    detailOpen: detail.detailOpen,
    detailLoading: detail.detailLoading,
    openDetail: detail.openDetail,
    closeDetail: detail.closeDetail,

    // wizard
    wizardState: wizard.wizardState,
    openCreate: wizard.openCreate,
    openEdit: wizard.openEdit,
    closeWizard: wizard.closeWizard,
    onWizardSuccess,

    // acciones
    onDelete,

    // shared
    sharedOpen: shared.sharedOpen,
    sharedLoading: shared.sharedLoading,
    sharedEvals: shared.sharedEvals,
    openShared: shared.openShared,
    closeShared: shared.closeShared,
    onClone: shared.onClone,
  };
}
